const LABOR_HOUR_SERVICE_CODE = "LABOR_HOUR";

function isLineSuppressed(line) {
  if (!line) {
    return false;
  }
  if (line.suppressed === true) {
    return true;
  }
  if (typeof line.suppressed === "string") {
    return line.suppressed.trim().toUpperCase() === "Y";
  }
  return false;
}

function findProfileDetailForLine(line, profileDetails) {
  const detailId = Number(line?.invProfileDetailId);
  if (!Number.isFinite(detailId) || detailId <= 0) {
    return null;
  }
  return (
    (profileDetails ?? []).find((row) => Number(row.invProfileDetailId) === detailId) ?? null
  );
}

function getServiceCodeForProfileDetail(profileDetail, contractServiceOptions) {
  const contractServiceId = Number(profileDetail?.contractServiceId);
  if (Number.isFinite(contractServiceId) && contractServiceId > 0) {
    const match = (contractServiceOptions ?? []).find(
      (opt) => Number(opt.contractServiceId) === contractServiceId
    );
    if (match) {
      return String(match.serviceCode ?? "").trim();
    }
  }
  const servicesRendered = String(profileDetail?.servicesRendered ?? "").trim();
  if (!servicesRendered) {
    return "";
  }
  const matches = (contractServiceOptions ?? []).filter(
    (opt) => String(opt.servicesRendered ?? "").trim() === servicesRendered
  );
  if (matches.length === 1) {
    return String(matches[0].serviceCode ?? "").trim();
  }
  return "";
}

export function isLaborHourProfileDetail(profileDetail, contractServiceOptions) {
  if (!profileDetail) {
    return false;
  }
  return (
    getServiceCodeForProfileDetail(profileDetail, contractServiceOptions).toUpperCase() ===
    LABOR_HOUR_SERVICE_CODE
  );
}

function normalizeProfileDetailOpCodes(profileDetail) {
  if (!profileDetail || !Array.isArray(profileDetail.opCodes)) {
    return [];
  }
  return profileDetail.opCodes
    .map((entry) => {
      if (typeof entry === "string") {
        const opCode = String(entry ?? "").trim();
        return opCode ? { opCode, jobNo: "", companyNumber: "" } : null;
      }
      const opCode = String(entry?.opCode ?? entry?.op_code ?? "").trim();
      if (!opCode) {
        return null;
      }
      return {
        opCode,
        jobNo: String(entry?.jobNo ?? entry?.job_no ?? "").trim(),
        companyNumber: String(entry?.companyNumber ?? entry?.company_number ?? "").trim(),
      };
    })
    .filter(Boolean);
}

function laborOpCodeHoursLookupKey(jobNo, opCode) {
  return `${String(jobNo ?? "").trim()}\0${String(opCode ?? "").trim()}`;
}

export function mergeLaborOpCodeHoursLookupEntries(...entryLists) {
  const seen = new Set();
  const entries = [];
  for (const list of entryLists) {
    for (const entry of list ?? []) {
      const jobNo = String(entry?.jobNo ?? "").trim();
      const opCode = String(entry?.opCode ?? "").trim();
      if (!jobNo || !opCode) {
        continue;
      }
      const key = laborOpCodeHoursLookupKey(jobNo, opCode);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      entries.push({ jobNo, opCode });
    }
  }
  return entries;
}

/**
 * Op-code pairs from act_inv_profile_opcodes for LABOR_HOUR invoice lines on this invoice.
 */
export function buildAllocatedLaborOpCodeHoursLookupEntries(
  invoiceLines,
  profileDetails,
  contractServiceOptions,
  fallbackJobNos = []
) {
  const jobNos = (fallbackJobNos ?? []).map((jobNo) => String(jobNo ?? "").trim()).filter(Boolean);
  const processedDetailIds = new Set();
  const seen = new Set();
  const entries = [];

  for (const line of invoiceLines ?? []) {
    if (isLineSuppressed(line)) {
      continue;
    }
    const detailId = Number(line.invProfileDetailId);
    if (!Number.isFinite(detailId) || detailId <= 0) {
      continue;
    }
    if (processedDetailIds.has(detailId)) {
      continue;
    }

    const profileDetail = findProfileDetailForLine(line, profileDetails);
    if (!isLaborHourProfileDetail(profileDetail, contractServiceOptions)) {
      continue;
    }

    processedDetailIds.add(detailId);

    for (const entry of normalizeProfileDetailOpCodes(profileDetail)) {
      const opCode = String(entry?.opCode ?? "").trim();
      if (!opCode) {
        continue;
      }
      const jobNosForEntry = entry.jobNo ? [entry.jobNo] : jobNos;
      for (const jobNo of jobNosForEntry) {
        const normalizedJobNo = String(jobNo ?? "").trim();
        if (!normalizedJobNo) {
          continue;
        }
        const key = laborOpCodeHoursLookupKey(normalizedJobNo, opCode);
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        entries.push({ jobNo: normalizedJobNo, opCode });
      }
    }
  }

  return entries;
}

export function formatLaborHoursMetric(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "0.0";
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(numeric);
}

export function formatVariancePercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "0.0%";
  }
  const sign = numeric > 0 ? "+" : "";
  return `${sign}${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(numeric)}%`;
}

export function formatAllocationProgressPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "0.0%";
  }
  return `${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(numeric)}%`;
}

/**
 * Sums labor hours from act_labor_nopcr_tran_stage for op codes on LABOR_HOUR invoice lines.
 */
export function sumAllocatedLaborHours(
  invoiceLines,
  profileDetails,
  contractServiceOptions,
  laborHoursByOpCodeKey,
  fallbackJobNos = []
) {
  const entries = buildAllocatedLaborOpCodeHoursLookupEntries(
    invoiceLines,
    profileDetails,
    contractServiceOptions,
    fallbackJobNos
  );
  let total = 0;
  for (const { jobNo, opCode } of entries) {
    const hours = laborHoursByOpCodeKey?.get?.(laborOpCodeHoursLookupKey(jobNo, opCode));
    if (Number.isFinite(Number(hours))) {
      total += Number(hours);
    }
  }
  return total;
}

/** Distinct jackets assigned to this invoice (profile-specific when available). */
export function buildInvoiceChargedJacketJobNos(invoiceProfileJobNos, programProfileJobNos) {
  const seen = new Set();
  const jobNos = [];
  const add = (value) => {
    const normalized = String(value ?? "").trim();
    if (!normalized) {
      return;
    }
    const key = normalized.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    jobNos.push(normalized);
  };

  for (const jobNo of invoiceProfileJobNos ?? []) {
    add(jobNo);
  }
  if (jobNos.length > 0) {
    return jobNos;
  }
  for (const jobNo of programProfileJobNos ?? []) {
    add(jobNo);
  }
  return jobNos;
}

/** Op codes configured in act_inv_profile_opcodes for the invoice program. */
export function buildMappedProfileOpCodeKeys(profileDetails) {
  const mapped = new Set();
  for (const detail of profileDetails ?? []) {
    if (!detail || !Array.isArray(detail.opCodes)) {
      continue;
    }
    for (const entry of detail.opCodes) {
      if (typeof entry === "string") {
        const opCode = String(entry ?? "").trim();
        if (opCode) {
          mapped.add(opCode.toUpperCase());
        }
        continue;
      }
      const opCode = String(entry?.opCode ?? entry?.op_code ?? "").trim();
      if (opCode) {
        mapped.add(opCode.toUpperCase());
      }
    }
  }
  return mapped;
}

function aggregateLaborHoursByOpCode(laborJacketOpCodeHoursResults) {
  const totals = new Map();
  for (const row of laborJacketOpCodeHoursResults ?? []) {
    const opCode = String(row?.opCode ?? "").trim();
    if (!opCode) {
      continue;
    }
    const hours = Number(row?.laborHours);
    if (!Number.isFinite(hours)) {
      continue;
    }
    const key = opCode.toUpperCase();
    totals.set(key, (totals.get(key) ?? 0) + hours);
  }
  return totals;
}

/**
 * Stage-table op codes for invoice jackets that are not in act_inv_profile_opcodes.
 */
export function buildUnmappedOpCodeRows(laborJacketOpCodeHoursResults, profileDetails) {
  const mappedOpCodes = buildMappedProfileOpCodeKeys(profileDetails);
  const hoursByOpCode = aggregateLaborHoursByOpCode(laborJacketOpCodeHoursResults);
  const rows = [];

  for (const [opCodeKey, hoursAvailable] of hoursByOpCode.entries()) {
    if (mappedOpCodes.has(opCodeKey)) {
      continue;
    }
    const displayOpCode =
      laborJacketOpCodeHoursResults.find(
        (row) => String(row?.opCode ?? "").trim().toUpperCase() === opCodeKey
      )?.opCode ?? opCodeKey;
    rows.push({
      opCode: String(displayOpCode).trim(),
      hoursAvailable,
      hoursNotAllocated: hoursAvailable,
    });
  }

  return rows.sort((a, b) =>
    a.opCode.localeCompare(b.opCode, undefined, { sensitivity: "base" })
  );
}

const VARIANCE_FULLY_ALLOCATED_EPSILON = 0.05;

export function computeLaborAllocationMetrics({ totalHoursCharged, totalHoursAllocated }) {
  const charged = Number.isFinite(Number(totalHoursCharged)) ? Number(totalHoursCharged) : 0;
  const allocated = Number.isFinite(Number(totalHoursAllocated))
    ? Number(totalHoursAllocated)
    : 0;

  let variancePercent = 0;
  if (charged > 0) {
    variancePercent = ((allocated - charged) / charged) * 100;
  }

  const allocationProgressPercent = charged > 0 ? (allocated / charged) * 100 : 0;
  const allocationProgressBarPercent = Math.min(Math.max(allocationProgressPercent, 0), 100);

  let varianceSubtitle = "fully allocated";
  let varianceTone = "balanced";
  if (Math.abs(variancePercent) > VARIANCE_FULLY_ALLOCATED_EPSILON) {
    if (variancePercent < 0) {
      varianceSubtitle = "under allocated";
      varianceTone = "under";
    } else {
      varianceSubtitle = "over allocated";
      varianceTone = "over";
    }
  }

  return {
    charged,
    allocated,
    variancePercent,
    allocationProgressPercent,
    allocationProgressBarPercent,
    varianceSubtitle,
    varianceTone,
  };
}
